import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PortalDsBadge, PortalDsSelect } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  ADMIN_SETTABLE_SUBSCRIPTION_TIERS,
  type AdminSettableSubscriptionTier,
  type PortalUserSubscriptionTier,
} from '../types/users.types';
import {
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
  isPaidSubscriptionTier,
} from '../utils/portal-users.mappers';

interface SubscriptionTierEditorProps {
  workspaceId: string | null;
  tier: PortalUserSubscriptionTier;
  disabled?: boolean;
  onSave: (
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => Promise<{ ok: boolean; message?: string }>;
}

export function SubscriptionTierEditor({
  workspaceId,
  tier,
  disabled = false,
  onSave,
}: SubscriptionTierEditorProps) {
  const [draftTier, setDraftTier] = useState<AdminSettableSubscriptionTier | ''>('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isPaidTier = isPaidSubscriptionTier(tier);
  const canEdit = Boolean(workspaceId) && !isPaidTier;

  useEffect(() => {
    if (ADMIN_SETTABLE_SUBSCRIPTION_TIERS.includes(tier as AdminSettableSubscriptionTier)) {
      setDraftTier(tier as AdminSettableSubscriptionTier);
      return;
    }
    setDraftTier('');
  }, [tier]);

  if (!workspaceId) {
    return (
      <PortalDsBadge variant="gray">
        {formatSubscriptionLabel(tier)}
      </PortalDsBadge>
    );
  }

  if (isPaidTier) {
    return (
      <div className="flex min-w-[140px] flex-col gap-1">
        <PortalDsBadge variant={getSubscriptionBadgeVariant(tier)}>
          {formatSubscriptionLabel(tier)}
        </PortalDsBadge>
        <span className="text-[11px] text-[#9CA3AF]">
          Paid via Stripe
        </span>
      </div>
    );
  }

  const handleSave = async() => {
    if (!workspaceId || !draftTier || draftTier === tier) {
      return;
    }

    setInlineError(null);
    setIsSaving(true);
    const result = await onSave(workspaceId, draftTier);
    setIsSaving(false);

    if (!result.ok) {
      setInlineError(result.message ?? 'Failed to update subscription tier.');
    }
  };

  return (
    <div className="flex min-w-[180px] flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <PortalDsSelect
          value={draftTier}
          disabled={disabled || isSaving || !canEdit}
          aria-label="Subscription tier"
          onChange={(event) => setDraftTier(event.target.value as AdminSettableSubscriptionTier)}
          className="h-8 min-w-[140px] text-[12px]"
        >
          {ADMIN_SETTABLE_SUBSCRIPTION_TIERS.map((option) => (
            <option
              key={option}
              value={option}
            >
              {formatSubscriptionLabel(option)}
            </option>
          ))}
        </PortalDsSelect>
        <button
          type="button"
          disabled={disabled || isSaving || !canEdit || !draftTier || draftTier === tier}
          onClick={() => void handleSave()}
          className="inline-flex h-8 items-center rounded-lg border border-[#D9DBE3] px-2 text-[12px] font-semibold text-[#0D0F1A] transition-colors hover:bg-[#F0F1F5] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2
              className="size-3.5 animate-spin"
              aria-hidden
            />
          ) : (
            'Save'
          )}
        </button>
      </div>
      {inlineError ? (
        <p className={cn('text-[11px] text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : (
        <span className="text-[11px] text-[#9CA3AF]">
          Portal-managed tier
        </span>
      )}
    </div>
  );
}
